import React, { useState, useEffect } from 'react';
import { getAuthenticatedAxios } from '../utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  TrendingUp, 
  TrendingDown,
  Users,
  DollarSign,
  Clock,
  ChevronDown,
  ChevronUp,
  Filter
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();
const BUYT_TOKEN = 'EQCWibYIG72ifoJlXD39iXia-frDwTvA0aeL7-pmmgvj3dwf';

const TokenTracker = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [sortBy, setSortBy] = useState('pnl'); // pnl, balance, avg_price
  const [filterType, setFilterType] = useState('all'); // all, our, others
  const [expandedHolder, setExpandedHolder] = useState(null);
  const [holderTransactions, setHolderTransactions] = useState(null);
  const [holderMetrics, setHolderMetrics] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, percent: 0, message: '' });
  const [holders, setHolders] = useState([]);
  const [eventSource, setEventSource] = useState(null);
  const [ourWalletsSet, setOurWalletsSet] = useState(new Set());

  useEffect(() => {
    loadData();
    loadOurWallets();
    return () => {
      // Cleanup: close EventSource on unmount
      if (eventSource) {
        eventSource.close();
      }
    };
  }, []);

  const loadOurWallets = async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/wallets/list`);
      if (response.data && response.data.wallets && Array.isArray(response.data.wallets)) {
        const addresses = response.data.wallets.map(w => {
          const addr = w.address || '';
          return addr.toLowerCase();
        }).filter(Boolean);
        setOurWalletsSet(new Set(addresses));
        console.log(`Loaded ${addresses.length} our wallets`);
      }
    } catch (error) {
      console.error('Failed to load our wallets:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setProgress({ current: 0, total: 0, percent: 0, message: 'Starting...' });
    setHolders([]);
    setData(null);
    
    // Close existing EventSource if any
    if (eventSource) {
      eventSource.close();
    }
    
    try {
      const limit = showAll ? 1000 : 50;
      // EventSource doesn't support custom headers, so we pass token as query parameter
      const authToken = localStorage.getItem('auth_token');
      const streamUrl = `/api/token-analytics/holders/${BUYT_TOKEN}/stream?limit=${limit}${authToken ? `&token=${encodeURIComponent(authToken)}` : ''}`;
      
      const es = new EventSource(streamUrl);
      setEventSource(es);
      
      let currentPrice = 0;
      let holdersList = [];
      
      es.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'status') {
            setProgress({
              current: message.progress || 0,
              total: message.total || 0,
              percent: message.total ? Math.round((message.progress / message.total) * 100) : 0,
              message: message.message || ''
            });
          } else if (message.type === 'price') {
            currentPrice = message.current_price;
            setData(prev => ({
              ...prev,
              current_price: currentPrice,
              timestamp: new Date().toISOString()
            }));
          } else if (message.type === 'holder') {
            // Add or update holder in list
            const holderIndex = holdersList.findIndex(h => h.address === message.holder.address);
            if (holderIndex >= 0) {
              holdersList[holderIndex] = message.holder;
            } else {
              holdersList.push(message.holder);
            }
            
            setHolders([...holdersList]);
            
            setProgress({
              current: message.progress,
              total: message.total,
              percent: message.progress_percent,
              message: `Processing holder ${message.progress}/${message.total}...`
            });
          } else if (message.type === 'complete') {
            // Final update with all data
            setData({
              token_address: message.token_address,
              current_price: message.current_price,
              timestamp: message.timestamp,
              total_holders: message.total_holders,
              total_tokens: message.total_tokens,
              total_pnl_usd: message.total_pnl_usd,
              our_wallets_count: message.our_wallets_count,
              our_total_tokens: message.our_total_tokens,
              our_total_pnl_usd: message.our_total_pnl_usd,
              holders: message.holders
            });
            setHolders(message.holders);
            setProgress({
              current: message.total_holders,
              total: message.total_holders,
              percent: 100,
              message: 'Complete!'
            });
            setLastUpdate(new Date());
            toast.success(`Loaded ${message.total_holders} holders`);
            es.close();
            setLoading(false);
          } else if (message.type === 'error') {
            toast.error(`Error: ${message.message}`);
            setProgress({
              current: 0,
              total: 0,
              percent: 0,
              message: `Error: ${message.message}`
            });
            es.close();
            setLoading(false);
          }
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };
      
      es.onerror = (error) => {
        console.error('EventSource error:', error);
        toast.error('Connection error. Trying fallback method...');
        es.close();
        // Fallback to regular API call
        loadDataFallback();
      };
      
    } catch (error) {
      console.error('Error setting up EventSource:', error);
      toast.error('Failed to start streaming. Trying fallback method...');
      loadDataFallback();
    }
  };
  
  const loadDataFallback = async () => {
    try {
      const limit = showAll ? 1000 : 50;
      const api = getAuthenticatedAxios();
      const response = await api.get(
        `${API_URL}/api/token-analytics/holders/${BUYT_TOKEN}?limit=${limit}`
      );

      if (response.data.success) {
        const apiData = response.data.data || response.data;
        
        if (apiData.error && apiData.holders?.length === 0) {
          toast.warning(`Could not fetch holders: ${apiData.error}`);
        } else if (apiData.holders?.length === 0) {
          toast.warning('No holders found for this token');
        } else {
          toast.success(`Loaded ${apiData.holders?.length || 0} holders`);
        }
        
        setData(apiData);
        setHolders(apiData.holders || []);
        setLastUpdate(new Date());
      } else {
        console.error('TokenTracker: API returned success=false', response.data);
        toast.error('Failed to load holder data: API error');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load holder data');
    } finally {
      setLoading(false);
    }
  };

  // Format address for display - show full address if user-friendly, truncated if raw
  const formatAddress = (addr) => {
    if (!addr) return '';
    // If user-friendly format (EQ/UQ), show truncated
    if (addr.startsWith('EQ') || addr.startsWith('UQ') || addr.startsWith('kQ')) {
      if (addr.length > 12) {
        return `${addr.substring(0, 6)}...${addr.substring(addr.length - 6)}`;
      }
      return addr;
    }
    // If raw format (0:hex), show more characters for readability
    if (addr.startsWith('0:') || addr.startsWith('-1:')) {
      if (addr.length > 20) {
        return `${addr.substring(0, 10)}...${addr.substring(addr.length - 8)}`;
      }
      return addr;
    }
    // Default truncation
    if (addr.length > 12) {
      return `${addr.substring(0, 6)}...${addr.substring(addr.length - 6)}`;
    }
    return addr;
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
    return num.toFixed(2);
  };

  const formatTime = (hours) => {
    if (hours === undefined || hours === null || isNaN(hours)) return '0h';
    if (hours < 24) return `${hours.toFixed(1)}h`;
    const days = hours / 24;
    if (days < 30) return `${days.toFixed(1)}d`;
    return `${(days / 30).toFixed(1)}mo`;
  };

  const loadHolderTransactions = async (walletAddress) => {
    setLoadingTransactions(true);
    try {
      // Encode address for URL
      const encodedAddress = encodeURIComponent(walletAddress);
      const api = getAuthenticatedAxios();
      const url = `${API_URL}/api/token/${BUYT_TOKEN}/wallet/${encodedAddress}/transactions`;
      
      console.log(`Loading transactions for wallet: ${walletAddress.substring(0, 30)}...`);
      
      const response = await api.get(url, { 
        params: { limit: 10000 },
        timeout: 30000  // 30 second timeout
      });
      
      console.log('Transaction response:', response.data);
      
      if (response.data) {
        const transactions = response.data.transactions || [];
        const metrics = response.data.metrics || null;
        
        console.log(`Loaded ${transactions.length} transactions`);
        console.log('Metrics:', metrics);
        
        setHolderTransactions(transactions);
        setHolderMetrics(metrics);
        
        if (transactions.length === 0 && (!metrics || (metrics.total_bought === 0 && metrics.total_sold === 0))) {
          toast.warning('Транзакции не найдены для этого кошелька');
        } else if (transactions.length > 0) {
          toast.success(`Загружено ${transactions.length} транзакций`);
        }
      }
    } catch (error) {
      console.error('Failed to load holder transactions:', error);
      console.error('Error response:', error.response?.data);
      toast.error(`Не удалось загрузить транзакции: ${error.response?.data?.detail || error.message}`);
      setHolderTransactions([]);
      setHolderMetrics(null);
    } finally {
      setLoadingTransactions(false);
    }
  };

  if (!data && loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin text-blue-600" />
            <p className="text-gray-600">Loading holder data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-gray-600 mb-4">No data available</p>
            <Button onClick={loadData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Load Holder Data
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Filter holders - use holders state if available, otherwise use data.holders
  const holdersToFilter = holders.length > 0 ? holders : (data?.holders || []);
  let filteredHolders = holdersToFilter;
  
  if (filterType === 'our') {
    filteredHolders = filteredHolders.filter(h => h.is_our_wallet);
  } else if (filterType === 'others') {
    filteredHolders = filteredHolders.filter(h => !h.is_our_wallet);
  }

  // Sort holders
  const sortedHolders = [...filteredHolders].sort((a, b) => {
    if (sortBy === 'pnl') {
      return (b.pnl_usd || 0) - (a.pnl_usd || 0);
    } else if (sortBy === 'balance') {
      return (b.balance_tokens || 0) - (a.balance_tokens || 0);
    } else if (sortBy === 'avg_price') {
      return (b.avg_buy_price || 0) - (a.avg_buy_price || 0);
    }
    return 0;
  });

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Token Holder Analytics</h1>
          <p className="text-gray-500 mt-1">
            {loading && progress.total > 0 ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                {progress.message} ({progress.current}/{progress.total} - {progress.percent}%)
              </span>
            ) : (
              <>
                BUYT Token · Current Price: ${data?.current_price?.toFixed(8) || '0.00000000'}
                {lastUpdate && (
                  <span className="ml-2 text-xs text-gray-400">
                    · Last updated: {new Date(lastUpdate).toLocaleTimeString()}
                  </span>
                )}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant={showAll ? 'default' : 'outline'}
            onClick={() => setShowAll(!showAll)}
            disabled={loading}
          >
            {showAll ? 'Show TOP 50' : 'Show All'}
          </Button>
          {loading && progress.total > 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress.percent}%` }}
                ></div>
              </div>
              <span className="font-medium">{progress.percent}%</span>
            </div>
          )}
          <Button onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Progress Card */}
      {loading && progress.total > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Loading Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">{progress.message}</span>
                <span className="font-medium">{progress.current} / {progress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div 
                  className="bg-blue-600 h-4 rounded-full transition-all duration-300 flex items-center justify-end pr-2"
                  style={{ width: `${progress.percent}%` }}
                >
                  <span className="text-xs text-white font-medium">{progress.percent}%</span>
                </div>
              </div>
              {holders.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {holders.length} holders loaded so far...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Total Holders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{data.total_holders || holders.length || 0}</div>
              {loading && holders.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {holders.length} loaded so far...
                </p>
              )}
            </CardContent>
          </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Total Tokens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(data.total_tokens || 0)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Total P/L
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(data.total_pnl_usd || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${Math.abs(data.total_pnl_usd || 0).toFixed(2)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Our Wallets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.our_wallets_count || 0}</div>
            <p className="text-xs text-gray-500 mt-1">
              P/L: <span className={(data.our_total_pnl_usd || 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                ${Math.abs(data.our_total_pnl_usd || 0).toFixed(2)}
              </span>
            </p>
          </CardContent>
        </Card>
        </div>
      )}

      {/* Our Wallets Summary */}
      {data && data.our_wallets_count > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg">Our Wallets Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Total Tokens</p>
                <p className="text-xl font-bold">{formatNumber(data.our_total_tokens || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Value</p>
                <p className="text-xl font-bold">
                  ${((data.our_total_tokens || 0) * (data.current_price || 0)).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total P/L</p>
                <p className={`text-xl font-bold ${(data.our_total_pnl_usd || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${Math.abs(data.our_total_pnl_usd || 0).toFixed(2)}
                  {data.our_total_pnl_usd !== 0 && (() => {
                    // Calculate percentage based on total invested
                    const totalInvested = data.our_total_tokens && data.current_price ? 
                      data.our_total_tokens * data.current_price - (data.our_total_pnl_usd || 0) : 0;
                    const pnlPercent = totalInvested > 0 ? 
                      ((data.our_total_pnl_usd || 0) / totalInvested) * 100 : 0;
                    return (
                      <span className="text-sm ml-2">
                        ({(data.our_total_pnl_usd || 0) >= 0 ? '+' : ''}{pnlPercent.toFixed(1)}%)
                      </span>
                    );
                  })()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">Filter:</span>
                <Button
                  size="sm"
                  variant={filterType === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilterType('all')}
                >
                  All ({holders.length > 0 ? holders.length : (data?.holders?.length || 0)})
                </Button>
                <Button
                  size="sm"
                  variant={filterType === 'our' ? 'default' : 'outline'}
                  onClick={() => setFilterType('our')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Our ({data?.our_wallets_count || holders.filter(h => h.is_our_wallet).length || 0})
                </Button>
                <Button
                  size="sm"
                  variant={filterType === 'others' ? 'default' : 'outline'}
                  onClick={() => setFilterType('others')}
                >
                  Others ({holders.length > 0 ? holders.filter(h => !h.is_our_wallet).length : ((data?.holders?.length || 0) - (data?.our_wallets_count || 0))})
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Sort by:</span>
              <Button
                size="sm"
                variant={sortBy === 'pnl' ? 'default' : 'outline'}
                onClick={() => setSortBy('pnl')}
              >
                P/L
              </Button>
              <Button
                size="sm"
                variant={sortBy === 'balance' ? 'default' : 'outline'}
                onClick={() => setSortBy('balance')}
              >
                Balance
              </Button>
              <Button
                size="sm"
                variant={sortBy === 'avg_price' ? 'default' : 'outline'}
                onClick={() => setSortBy('avg_price')}
              >
                Avg Price
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Holders Table */}
      <Card>
        <CardHeader>
          <CardTitle>Holders ({sortedHolders.length})</CardTitle>
          <CardDescription>
            {loading && holders.length > 0 ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading... {holders.length} holders loaded so far
              </span>
            ) : (
              'Click on a holder to see detailed trade history'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && holders.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin text-blue-600" />
              <p>Loading holders...</p>
              {progress.message && <p className="text-sm mt-2">{progress.message}</p>}
            </div>
          )}
          
          {!loading && sortedHolders.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p className="text-lg font-medium mb-2">No holders found</p>
              {data?.error ? (
                <div className="space-y-2">
                  <p className="text-sm text-red-600">Error: {data.error}</p>
                  <p className="text-xs text-gray-400">
                    This might be due to TonAPI rate limits. Please try again later or configure an API key.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm">Total holders: {data?.total_holders || 0}</p>
                  <p className="text-xs">
                    Filter: {filterType} | 
                    Available: {data?.holders?.length || holders.length || 0}
                  </p>
                  {filterType !== 'all' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setFilterType('all')}
                      className="mt-4"
                    >
                      Show All Holders
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {sortedHolders.length > 0 && (
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Wallet</TableHead>
                <TableHead>Balance (Tokens)</TableHead>
                <TableHead>Balance (USD)</TableHead>
                <TableHead>Avg Buy Price</TableHead>
                <TableHead>P/L (USD)</TableHead>
                <TableHead>P/L (%)</TableHead>
                <TableHead>Price Change</TableHead>
                <TableHead>Holding Time</TableHead>
                <TableHead>Trades</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedHolders.map((holder, index) => (
                <React.Fragment key={holder.address}>
                  <TableRow 
                    className={`
                      cursor-pointer hover:bg-gray-50 
                      ${holder.is_our_wallet || ourWalletsSet.has((holder.address_raw || holder.address)?.toLowerCase() || '') ? 'bg-green-50' : ''}
                      ${expandedHolder === holder.address ? 'border-b-0' : ''}
                    `}
                    onClick={async () => {
                      const newExpanded = expandedHolder === holder.address ? null : holder.address;
                      setExpandedHolder(newExpanded);
                      
                      // Load transactions when expanding
                      if (newExpanded) {
                        // Use raw address if available (for API calls), otherwise use address as-is
                        const addressToUse = holder.address_raw || holder.address;
                        await loadHolderTransactions(addressToUse);
                      } else {
                        setHolderTransactions(null);
                        setHolderMetrics(null);
                      }
                    }}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {expandedHolder === holder.address ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                        <span className="font-mono text-sm" title={holder.address}>
                          {formatAddress(holder.address)}
                        </span>
                        {(holder.is_our_wallet || ourWalletsSet.has((holder.address_raw || holder.address)?.toLowerCase() || '')) && (
                          <Badge variant="default" className="text-xs bg-green-600">OUR</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatNumber(holder.balance_tokens || 0)}
                    </TableCell>
                    <TableCell>${(holder.balance_usd || 0).toFixed(2)}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {holder.avg_buy_price_ton !== undefined ? (
                        <div>
                          <div>{(holder.avg_buy_price_ton || 0).toFixed(8)} TON</div>
                          <div className="text-xs text-gray-500">${(holder.avg_buy_price_usd || holder.avg_buy_price || 0).toFixed(8)}</div>
                        </div>
                      ) : (
                        <div>${(holder.avg_buy_price || 0).toFixed(8)}</div>
                      )}
                    </TableCell>
                    <TableCell className={(holder.pnl_ton !== undefined ? holder.pnl_ton : holder.pnl_usd || 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                      <div className="font-bold">
                        {holder.pnl_ton !== undefined ? (
                          <div>
                            <div>{(holder.pnl_ton || 0) >= 0 ? '+' : ''}{(holder.pnl_ton || 0).toFixed(4)} TON</div>
                            <div className="text-xs">${(holder.pnl_usd || 0) >= 0 ? '+' : ''}${(holder.pnl_usd || 0).toFixed(2)}</div>
                          </div>
                        ) : (
                          <div>{(holder.pnl_usd || 0) >= 0 ? '+' : ''}${(holder.pnl_usd || 0).toFixed(2)}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={(holder.pnl_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                      <div className="font-bold">
                        {(holder.pnl_percent || 0) >= 0 ? '+' : ''}{(holder.pnl_percent || 0).toFixed(1)}%
                      </div>
                    </TableCell>
                    <TableCell className={(holder.price_change_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {(holder.price_change_percent || 0) >= 0 ? '+' : ''}{(holder.price_change_percent || 0).toFixed(1)}%
                    </TableCell>
                    <TableCell className="flex items-center gap-1">
                      <Clock className="h-3 w-3 text-gray-400" />
                      {formatTime(holder.holding_time_hours || 0)}
                    </TableCell>
                    <TableCell>
                      <span className="text-green-600">{holder.buy_count || 0}↑</span>
                      {' / '}
                      <span className="text-red-600">{holder.sell_count || 0}↓</span>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Trade History */}
                  {expandedHolder === holder.address && (
                    <TableRow>
                      <TableCell colSpan={9} className="bg-gray-50 p-4">
                        {loadingTransactions ? (
                          <div className="flex items-center justify-center py-8">
                            <RefreshCw className="h-5 w-5 animate-spin text-blue-600 mr-2" />
                            <span className="text-gray-600">Загрузка транзакций...</span>
                          </div>
                        ) : holderMetrics ? (
                          <div className="space-y-4">
                            {/* Metrics Summary */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                              <div className="bg-white p-3 rounded border">
                                <div className="text-xs text-gray-500 mb-1">Средняя цена закупки</div>
                                <div className="font-semibold">
                                  {holderMetrics.avg_buy_price_ton > 0 ? (
                                    <>
                                      <div>{holderMetrics.avg_buy_price_ton.toFixed(8)} TON</div>
                                      <div className="text-xs text-gray-500">${holderMetrics.avg_buy_price_usd.toFixed(8)}</div>
                                    </>
                                  ) : (
                                    <span className="text-gray-400">Нет покупок</span>
                                  )}
                                </div>
                              </div>
                              <div className="bg-white p-3 rounded border">
                                <div className="text-xs text-gray-500 mb-1">Текущий PNL</div>
                                <div className={`font-semibold ${holderMetrics.pnl_usd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {holderMetrics.current_balance > 0 ? (
                                    <>
                                      <div>{holderMetrics.pnl_ton >= 0 ? '+' : ''}{holderMetrics.pnl_ton.toFixed(4)} TON</div>
                                      <div className="text-xs">${holderMetrics.pnl_usd >= 0 ? '+' : ''}${holderMetrics.pnl_usd.toFixed(2)}</div>
                                      <div className="text-xs">{holderMetrics.pnl_percent >= 0 ? '+' : ''}{holderMetrics.pnl_percent.toFixed(1)}%</div>
                                    </>
                                  ) : (
                                    <span className="text-gray-400">Нет позиции</span>
                                  )}
                                </div>
                              </div>
                              <div className="bg-white p-3 rounded border">
                                <div className="text-xs text-gray-500 mb-1">Время в сделке</div>
                                <div className="font-semibold">
                                  {formatTime(holderMetrics.holding_time_hours)}
                                </div>
                                {holderMetrics.first_buy_time && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    С {new Date(holderMetrics.first_buy_time).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                              <div className="bg-white p-3 rounded border">
                                <div className="text-xs text-gray-500 mb-1">Текущий баланс</div>
                                <div className="font-semibold">
                                  {formatNumber(holderMetrics.current_balance)} токенов
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  Куплено: {formatNumber(holderMetrics.total_bought)} | Продано: {formatNumber(holderMetrics.total_sold)}
                                </div>
                              </div>
                            </div>

                            {/* Transactions List */}
                            <div>
                              <h4 className="font-semibold mb-3">
                                Все транзакции ({holderTransactions?.length || 0})
                              </h4>
                              {holderTransactions && holderTransactions.length > 0 ? (
                                <div className="space-y-1 max-h-96 overflow-y-auto">
                                  {holderTransactions.map((tx, idx) => (
                                    <div 
                                      key={idx}
                                      className={`flex items-center justify-between p-2 rounded text-sm ${
                                        tx.type === 'buy' ? 'bg-green-50' : 'bg-red-50'
                                      }`}
                                    >
                                      <div className="flex items-center gap-3">
                                        {tx.type === 'buy' ? (
                                          <TrendingUp className="h-4 w-4 text-green-600" />
                                        ) : (
                                          <TrendingDown className="h-4 w-4 text-red-600" />
                                        )}
                                        <span className={`font-medium ${tx.type === 'buy' ? 'text-green-700' : 'text-red-700'}`}>
                                          {tx.type.toUpperCase()}
                                        </span>
                                        <span className="text-gray-600 text-xs">
                                          {new Date(tx.timestamp).toLocaleString()}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-4 text-xs">
                                        <span>{formatNumber(tx.token_amount || 0)} токенов</span>
                                        <span className="font-mono">{(tx.price_per_token_ton || 0).toFixed(8)} TON</span>
                                        <span className="font-medium">{(tx.ton_amount || 0).toFixed(4)} TON</span>
                                        {tx.tx_hash && (
                                          <a 
                                            href={`https://tonscan.org/tx/${tx.tx_hash}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline"
                                          >
                                            TX
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-8 text-gray-500">
                                  <p>Нет транзакций для этого кошелька</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <p>Нет данных о транзакциях</p>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TokenTracker;
